import { OPERATOR } from "@/lib/operator";
import type { LegalDoc } from "@/lib/legal/terms";

/**
 * Privacy Policy.
 *
 * German authoritative, English translation. Drafted against:
 *   revFADP (revDSG), in force 1 September 2023 — Art. 19 (information duty),
 *     Art. 16/17 (disclosure abroad), Art. 24 (breach notification to the FDPIC),
 *     Art. 25-28 (access, portability), Art. 30 (processing principles)
 *   GDPR — Art. 6 (legal bases), Art. 13/14 (information), Art. 15-22 (rights),
 *     Art. 28 (processors), Art. 44 ff. (transfers), Art. 33/34 (breaches)
 *   EU AI Act Art. 50 (disclosure that a user interacts with an AI system)
 *
 * The most important distinction in this document, and the one most privacy
 * policies of this kind get wrong: for the customer's own account we are the
 * controller, but for the people who talk to a customer's Twin we are only the
 * processor and the customer is the controller. Blurring that would misstate
 * who a data subject must approach.
 *
 * NOT legal advice, and not reviewed by external counsel.
 */

export const PRIVACY_VERSION = "2026-08";
export const PRIVACY_EFFECTIVE_DE = "7. August 2026";
export const PRIVACY_EFFECTIVE_EN = "7 August 2026";

const OP = `${OPERATOR.legalName}, ${OPERATOR.street}, ${OPERATOR.postalCode} ${OPERATOR.city}, Schweiz`;

export const PRIVACY_DE: LegalDoc = {
  title: "Datenschutzerklärung",
  version: PRIVACY_VERSION,
  effective: PRIVACY_EFFECTIVE_DE,
  intro:
    "Diese Erklärung beschreibt, welche Personendaten wir bearbeiten, zu welchen Zwecken und auf welcher Rechtsgrundlage. Verbindlich ist die deutsche Fassung.",
  sections: [
    {
      h2: "1. Verantwortliche Stelle und Kontakt",
      body: [
        `Verantwortlich für die Bearbeitung von Personendaten im Zusammenhang mit ${OPERATOR.project} ist ${OP}, Inhaber ${OPERATOR.owner}.`,
        `Für Fragen zum Datenschutz und zur Ausübung von Rechten: ${OPERATOR.email}.`,
        "Wir haben keine Datenschutzberaterin und keinen Datenschutzberater im Sinne von Art. 10 revDSG bestellt, da hierzu keine Pflicht besteht. Anfragen richten Sie bitte an die vorstehende Adresse.",
        "Hinweis: Wir prüfen laufend, ob für uns eine Vertretung in der EU nach Art. 27 DSGVO erforderlich ist. Solange keine benannt ist, wenden Sie sich bitte direkt an die vorstehende Adresse.",
      ],
    },
    {
      h2: "2. Geltungsbereich",
      body: [
        "Diese Erklärung gilt für unsere Websites, die Anwendung, die Programmierschnittstellen und die damit verbundenen Dienste.",
        "Sie gilt nicht für Websites Dritter, auf die wir verlieren. Für deren Datenbearbeitung sind ausschliesslich die jeweiligen Betreiber verantwortlich.",
        "Anwendbar sind das schweizerische Datenschutzgesetz (revDSG) und, soweit unsere Bearbeitung in den räumlichen Anwendungsbereich fällt, die Datenschutz-Grundverordnung (DSGVO).",
      ],
    },
    {
      h2: "3. Unsere Rolle: Verantwortliche oder Auftragsbearbeiterin",
      body: [
        "Für die Daten unserer Kundinnen und Kunden — Konto, Vertrag, Abrechnung, Support, Nutzung der Anwendung — sind wir Verantwortliche im Sinne von Art. 5 lit. j revDSG bzw. Art. 4 Nr. 7 DSGVO.",
        "Für Daten von Personen, die mit dem AI Twin eines Kunden sprechen oder diesem ihre Kontaktdaten übermitteln, sind wir Auftragsbearbeiterin. Verantwortlich ist in diesem Fall der Kunde, der den Twin betreibt. Rechte betreffend diese Daten sind gegenüber dem jeweiligen Kunden geltend zu machen; auf Anfrage nennen wir die zuständige Stelle, soweit uns dies möglich ist.",
        "Grundlage der Auftragsbearbeitung ist unser Auftragsbearbeitungsvertrag (DPA), der die Anforderungen von Art. 9 revDSG und Art. 28 DSGVO umsetzt.",
      ],
    },
    {
      h2: "4. Welche Daten wir bearbeiten",
      body: [
        "Kontodaten: Name, E-Mail-Adresse, Passwort in gehashter Form, Sprache, Zeitpunkt der Registrierung und der letzten Anmeldung, Zustimmung zu AGB und Datenschutzerklärung mit Zeitpunkt und Dokumentversion.",
        "Unternehmensdaten: Firmenname, Rechtsform, Adresse, Land, Branche, Website, Logo, Beschreibung, Angaben zur Verifizierung sowie — bei Nutzung des Marktplatzes — Rechnungs- und Auszahlungsangaben einschliesslich IBAN und Kontoinhaber.",
        "Inhaltsdaten: Von Ihnen bereitgestellte Texte, Dokumente und Website-Inhalte, deren maschinelle Repräsentationen (Einbettungen) sowie die daraus erzeugten Antworten.",
        "Nutzungs- und Protokolldaten: Aufrufe, Zeitstempel, IP-Adresse, Browserkennung, Fehlerprotokolle, Sicherheitsereignisse.",
        "Kommunikationsdaten: Support-Anfragen, E-Mail-Verkehr, Angaben aus Kontaktformularen.",
        "Signaturdaten: Bei elektronischer Unterzeichnung von Verträgen erfassen wir Name der unterzeichnenden Person, Zeitpunkt, IP-Adresse, Browserkennung, E-Mail-Adresse sowie einen kryptografischen Prüfwert des unterzeichneten Textes. Diese Daten dienen dem Nachweis der Unterschrift.",
        "Zahlungsdaten: Zahlungsstatus, Betrag, Währung, Referenz. Vollständige Kartendaten werden ausschliesslich beim Zahlungsdienstleister bearbeitet; wir erhalten sie nicht.",
      ],
    },
    {
      h2: "5. Zwecke und Rechtsgrundlagen",
      body: [
        "Erbringung des Dienstes, Bereitstellung des Kontos, Erzeugung und Betrieb des Twin: Vertragserfüllung (Art. 31 Abs. 2 lit. a revDSG; Art. 6 Abs. 1 lit. b DSGVO).",
        "Abrechnung, Zahlungsabwicklung und Auszahlungen: Vertragserfüllung sowie Erfüllung gesetzlicher Pflichten (Art. 6 Abs. 1 lit. b und lit. c DSGVO).",
        "Sicherheit, Missbrauchsabwehr, Protokollierung, Ratenbegrenzung: berechtigtes Interesse am sicheren Betrieb (Art. 31 Abs. 1 revDSG; Art. 6 Abs. 1 lit. f DSGVO).",
        "Support und Kommunikation: Vertragserfüllung und berechtigtes Interesse an der Bearbeitung von Anfragen.",
        "Produktverbesserung anhand aggregierter, nicht personenbezogener Auswertungen: berechtigtes Interesse.",
        "Nachweis von Zustimmungen und Unterschriften: berechtigtes Interesse an der Beweisführung sowie Erfüllung gesetzlicher Aufbewahrungspflichten.",
        "Produktbezogene E-Mails und Newsletter: Einwilligung, soweit erforderlich (Art. 6 Abs. 1 lit. a DSGVO), jederzeit widerrufbar über den Abmeldelink oder die Benachrichtigungseinstellungen.",
        "Erfüllung gesetzlicher Pflichten, insbesondere buchhalterischer und steuerlicher Aufbewahrungspflichten: Art. 6 Abs. 1 lit. c DSGVO.",
      ],
    },
    {
      h2: "6. Bearbeitung durch Sprachmodelle",
      body: [
        "Zur Erzeugung von Antworten übermitteln wir Anfragen und relevante Ausschnitte der Wissensbasis an Anbieter von Sprachmodellen. Diese Anbieter handeln als Auftragsbearbeiter beziehungsweise Unterauftragsbearbeiter.",
        "Geben Sie in einer Konversation Personendaten ein, werden diese im Rahmen der Anfrage an den Modellanbieter übermittelt. Geben Sie keine Angaben ein, die nicht verarbeitet werden sollen.",
        "Wir verwenden Ihre Inhalte nicht zum Training allgemeiner Sprachmodelle und haben mit den eingesetzten Anbietern entsprechende Bedingungen vereinbart, soweit deren Angebot dies vorsieht.",
        "Nutzerinnen und Nutzer werden darauf hingewiesen, dass sie mit einem KI-System interagieren.",
        "Antworten des Twin werden mit Zeitstempel, Quellenangaben und Prüfwert protokolliert, um Nachvollziehbarkeit und Nachweisbarkeit sicherzustellen.",
      ],
    },
    {
      h2: "7. Cookies und ähnliche Technologien",
      body: [
        "Wir setzen technisch notwendige Cookies ein, um die Anmeldung, die Sitzungsverwaltung, die Sprachwahl und die Sicherheit zu gewährleisten. Diese sind für den Betrieb erforderlich; Rechtsgrundlage ist unser berechtigtes Interesse am funktionsfähigen Dienst.",
        "Die Spracheinstellung wird in einem Cookie sowie im lokalen Speicher des Browsers abgelegt, damit die gewählte Sprache erhalten bleibt.",
        "Cookies können über die Browsereinstellungen gelöscht oder blockiert werden. Werden notwendige Cookies blockiert, ist der Dienst nicht oder nur eingeschränkt nutzbar.",
      ],
    },
    {
      h2: "8. Empfängerinnen und Unterauftragsbearbeiter",
      body: [
        "Wir geben Personendaten an sorgfältig ausgewählte Dienstleister weiter, die uns beim Betrieb unterstützen: Hosting und Datenbank, Content-Delivery und Schutz vor Überlastung, Anbieter von Sprachmodellen, Zahlungsabwicklung sowie Versand transaktionaler E-Mails.",
        "Die vollständige, jeweils aktuelle Liste der Unterauftragsbearbeiter mit Zweck und Region ist auf der Seite Unterauftragsbearbeiter öffentlich einsehbar.",
        "Eine Weitergabe erfolgt im Übrigen nur, wenn Sie eingewilligt haben, wenn wir gesetzlich oder behördlich dazu verpflichtet sind, oder wenn dies zur Geltendmachung oder Verteidigung von Rechtsansprüchen erforderlich ist.",
        "Wir verkaufen keine Personendaten.",
      ],
    },
    {
      h2: "9. Bekanntgabe ins Ausland",
      body: [
        "Anwendungsdaten werden in der Europäischen Union (Irland) gespeichert. Für die Europäische Union besteht aus schweizerischer Sicht ein angemessener Schutz im Sinne von Art. 16 Abs. 1 revDSG.",
        "Für die Erzeugung von Antworten sowie für einzelne Infrastrukturleistungen können Daten in Staaten ohne von der Schweiz oder der EU anerkannten angemessenen Schutz übermittelt werden, namentlich in die Vereinigten Staaten.",
        "Solche Übermittlungen stützen wir auf die Standardvertragsklauseln der Europäischen Kommission in der vom EDÖB anerkannten Fassung oder auf einen anderen zulässigen Mechanismus nach Art. 16 f. revDSG und Art. 46 DSGVO, ergänzt um geeignete zusätzliche Massnahmen.",
      ],
    },
    {
      h2: "10. Aufbewahrung und Löschung",
      body: [
        "Wir bewahren Personendaten so lange auf, wie es für die genannten Zwecke erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.",
        "Kontodaten und Inhalte werden nach Beendigung des Vertrags während 30 Tagen zum Export bereitgehalten und anschliessend gelöscht.",
        "Belege mit buchhalterischer Relevanz, einschliesslich Rechnungen und Auszahlungsnachweisen, bewahren wir zehn Jahre auf (Art. 958f OR).",
        "Nachweise über Zustimmungen und elektronische Unterschriften bewahren wir für die Dauer der Verjährungsfristen der betreffenden Ansprüche auf, in der Regel zehn Jahre (Art. 127 OR).",
        "Sicherheits- und Serverprotokolle werden in der Regel nach 90 Tagen gelöscht oder anonymisiert, soweit sie nicht zur Aufklärung eines konkreten Vorfalls benötigt werden.",
      ],
    },
    {
      h2: "11. Datensicherheit",
      body: [
        "Wir treffen angemessene technische und organisatorische Massnahmen zum Schutz vor unbefugtem Zugriff, Verlust und Missbrauch. Dazu gehören Transportverschlüsselung, Verschlüsselung ruhender Daten beim Hosting-Anbieter, mandantengetrennte Datenhaltung mit zeilenbasierter Zugriffskontrolle, Zugriffsbeschränkungen nach dem Prinzip der geringsten Rechte sowie regelmässige Sicherungen.",
        "Eine absolute Sicherheit kann bei der Übermittlung über das Internet nicht gewährleistet werden.",
        "Bei einer Verletzung der Datensicherheit, die voraussichtlich zu einem hohen Risiko für die betroffenen Personen führt, informieren wir den EDÖB nach Art. 24 revDSG sowie, im Anwendungsbereich der DSGVO, die zuständige Aufsichtsbehörde nach Art. 33 DSGVO und, soweit erforderlich, die betroffenen Personen.",
      ],
    },
    {
      h2: "12. Ihre Rechte",
      body: [
        "Sie haben das Recht auf Auskunft über die von uns bearbeiteten Personendaten (Art. 25 revDSG; Art. 15 DSGVO).",
        "Sie haben das Recht auf Berichtigung unrichtiger Daten (Art. 32 revDSG; Art. 16 DSGVO) sowie auf Löschung und auf Einschränkung der Bearbeitung, soweit die gesetzlichen Voraussetzungen erfüllt sind (Art. 17 und 18 DSGVO).",
        "Sie haben das Recht auf Herausgabe oder Übertragung Ihrer Daten in einem gängigen elektronischen Format (Art. 28 revDSG; Art. 20 DSGVO).",
        "Sie können der Bearbeitung widersprechen, soweit wir sie auf ein berechtigtes Interesse stützen (Art. 30 Abs. 2 revDSG; Art. 21 DSGVO).",
        "Eine erteilte Einwilligung können Sie jederzeit mit Wirkung für die Zukunft widerrufen.",
        "Zur Ausübung genügt eine Mitteilung an die unter Ziffer 1 genannte Adresse. Zur Verhinderung von Missbrauch können wir einen Identitätsnachweis verlangen. Wir antworten innert 30 Tagen; ist dies nicht möglich, informieren wir Sie über die Gründe und die voraussichtliche Dauer.",
      ],
    },
    {
      h2: "13. Beschwerderecht",
      body: [
        "Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren.",
        "In der Schweiz: Eidgenössischer Datenschutz- und Öffentlichkeitsbeauftragter (EDÖB), Feldeggweg 1, 3003 Bern.",
        "Im Anwendungsbereich der DSGVO: die Aufsichtsbehörde Ihres gewöhnlichen Aufenthaltsorts, Ihres Arbeitsplatzes oder des Orts des mutmasslichen Verstosses.",
      ],
    },
    {
      h2: "14. Automatisierte Entscheidungen und Profiling",
      body: [
        "Wir treffen keine ausschliesslich automatisierten Einzelentscheidungen mit rechtlicher Wirkung oder ähnlich erheblicher Beeinträchtigung im Sinne von Art. 21 revDSG bzw. Art. 22 DSGVO.",
        "Der Dienst erstellt automatisiert Bewertungen, etwa eine Einschätzung der Passung zwischen Unternehmen oder eine Priorisierung von Anfragen. Diese Bewertungen sind Entscheidungshilfen für die Nutzerinnen und Nutzer; die Entscheidung trifft stets ein Mensch.",
        "Grundlage und Zusammensetzung solcher Bewertungen werden im Produkt offengelegt, damit sie nachvollziehbar bleiben.",
      ],
    },
    {
      h2: "15. Daten von Besucherinnen und Besuchern eines Twin",
      body: [
        "Wenn Sie mit dem Twin eines Unternehmens sprechen, werden Ihre Eingaben und allfällige Kontaktangaben an dieses Unternehmen übermittelt. Verantwortlich für die weitere Bearbeitung ist dieses Unternehmen, nicht wir.",
        "Wir bearbeiten diese Daten ausschliesslich im Auftrag und nach Weisung des betreffenden Unternehmens sowie zur Sicherstellung des sicheren Betriebs.",
        "Ihre Rechte machen Sie gegenüber dem betreffenden Unternehmen geltend. Kontaktieren Sie uns dennoch, leiten wir Ihre Anfrage weiter oder nennen Ihnen die zuständige Stelle, soweit uns dies möglich ist.",
      ],
    },
    {
      h2: "16. Änderungen dieser Erklärung",
      body: [
        "Wir passen diese Erklärung an, wenn sich unsere Bearbeitung, die eingesetzten Dienstleister oder die Rechtslage ändern.",
        "Die jeweils gültige Fassung ist mit Versionsbezeichnung und Datum auf dieser Seite abrufbar. Bei wesentlichen Änderungen informieren wir Kundinnen und Kunden zusätzlich in Textform.",
        `Fassung ${PRIVACY_VERSION}, gültig ab ${PRIVACY_EFFECTIVE_DE}.`,
      ],
    },
    {
      h2: "17. Sprachfassungen",
      body: [
        "Diese Datenschutzerklärung besteht in deutscher und englischer Sprache. Bei Abweichungen ist die deutsche Fassung massgebend.",
        `Kontakt: ${OP}, ${OPERATOR.email}.`,
      ],
    },
  ],
};

export const PRIVACY_EN: LegalDoc = {
  title: "Privacy Policy",
  version: PRIVACY_VERSION,
  effective: PRIVACY_EFFECTIVE_EN,
  intro:
    "This policy describes which personal data we process, for which purposes and on which legal basis. The German version is authoritative.",
  sections: [
    {
      h2: "1. Controller and contact",
      body: [
        `The controller for the processing of personal data in connection with ${OPERATOR.project} is ${OP}, owner ${OPERATOR.owner}.`,
        `For data protection questions and to exercise your rights: ${OPERATOR.email}.`,
        "We have not appointed a data protection adviser within the meaning of Art. 10 revFADP, as there is no obligation to do so. Please direct enquiries to the address above.",
        "Note: we keep under review whether a representative in the EU under Art. 27 GDPR is required for us. While none is designated, please contact the address above directly.",
      ],
    },
    {
      h2: "2. Scope",
      body: [
        "This policy applies to our websites, the application, the programming interfaces and the associated services.",
        "It does not apply to third-party websites we link to. Their operators alone are responsible for their processing.",
        "The Swiss Federal Act on Data Protection (revFADP) applies and, where our processing falls within its territorial scope, the General Data Protection Regulation (GDPR).",
      ],
    },
    {
      h2: "3. Our role: controller or processor",
      body: [
        "For our customers' data — account, contract, billing, support, use of the application — we are the controller within the meaning of Art. 5(j) revFADP and Art. 4(7) GDPR.",
        "For data of people who talk to a customer's AI Twin or provide it with their contact details, we are the processor. The controller in that case is the customer operating the Twin. Rights concerning that data must be exercised against that customer; on request we will name the responsible entity where we are able to.",
        "Processing on behalf is governed by our Data Processing Addendum, which implements the requirements of Art. 9 revFADP and Art. 28 GDPR.",
      ],
    },
    {
      h2: "4. Which data we process",
      body: [
        "Account data: name, email address, password in hashed form, language, time of registration and last sign-in, acceptance of the Terms and this policy with timestamp and document version.",
        "Company data: company name, legal form, address, country, industry, website, logo, description, verification details and — when using the marketplace — invoicing and payout details including IBAN and account holder.",
        "Content data: texts, documents and website content you provide, their machine representations (embeddings) and the answers generated from them.",
        "Usage and log data: requests, timestamps, IP address, browser identification, error logs, security events.",
        "Communication data: support requests, email correspondence, information from contact forms.",
        "Signature data: when contracts are signed electronically we record the signatory's name, the time, the IP address, the browser identification, the email address and a cryptographic checksum of the signed text. This data serves as evidence of the signature.",
        "Payment data: payment status, amount, currency, reference. Full card details are processed exclusively by the payment service provider; we do not receive them.",
      ],
    },
    {
      h2: "5. Purposes and legal bases",
      body: [
        "Providing the Service, the account, and generating and operating the Twin: performance of the contract (Art. 31(2)(a) revFADP; Art. 6(1)(b) GDPR).",
        "Billing, payment processing and payouts: performance of the contract and compliance with legal obligations (Art. 6(1)(b) and (c) GDPR).",
        "Security, abuse prevention, logging, rate limiting: legitimate interest in secure operation (Art. 31(1) revFADP; Art. 6(1)(f) GDPR).",
        "Support and communication: performance of the contract and legitimate interest in handling enquiries.",
        "Product improvement based on aggregated, non-personal analysis: legitimate interest.",
        "Evidence of consents and signatures: legitimate interest in being able to prove them, and compliance with statutory retention duties.",
        "Product emails and newsletters: consent where required (Art. 6(1)(a) GDPR), revocable at any time via the unsubscribe link or the notification settings.",
        "Compliance with legal obligations, in particular accounting and tax retention duties: Art. 6(1)(c) GDPR.",
      ],
    },
    {
      h2: "6. Processing by language models",
      body: [
        "To generate answers we transmit requests and relevant excerpts of the knowledge base to language model providers. These providers act as processors or sub-processors.",
        "If you enter personal data into a conversation, it is transmitted to the model provider as part of the request. Please do not enter information you do not want processed.",
        "We do not use your content to train general language models and have agreed corresponding terms with the providers we use, where their offering provides for this.",
        "Users are informed that they are interacting with an AI system.",
        "The Twin's answers are logged with a timestamp, source attribution and a checksum, to ensure traceability and evidential value.",
      ],
    },
    {
      h2: "7. Cookies and similar technologies",
      body: [
        "We use technically necessary cookies to enable sign-in, session management, language selection and security. These are required for operation; the legal basis is our legitimate interest in a functioning service.",
        "The language setting is stored in a cookie and in the browser's local storage so that the chosen language persists.",
        "Cookies can be deleted or blocked via your browser settings. If necessary cookies are blocked, the Service cannot be used, or only to a limited extent.",
      ],
    },
    {
      h2: "8. Recipients and sub-processors",
      body: [
        "We pass personal data to carefully selected service providers that support our operations: hosting and database, content delivery and overload protection, language model providers, payment processing and transactional email delivery.",
        "The complete, current list of sub-processors with purpose and region is publicly available on the sub-processors page.",
        "Otherwise we disclose data only where you have consented, where we are legally or officially required to, or where necessary to establish or defend legal claims.",
        "We do not sell personal data.",
      ],
    },
    {
      h2: "9. Disclosure abroad",
      body: [
        "Application data is stored in the European Union (Ireland). From a Swiss perspective the European Union provides adequate protection within the meaning of Art. 16(1) revFADP.",
        "To generate answers and for individual infrastructure services, data may be transferred to countries without protection recognised as adequate by Switzerland or the EU, in particular the United States.",
        "We base such transfers on the European Commission's Standard Contractual Clauses in the version recognised by the FDPIC, or on another permitted mechanism under Art. 16 f. revFADP and Art. 46 GDPR, supplemented by appropriate additional measures.",
      ],
    },
    {
      h2: "10. Retention and deletion",
      body: [
        "We retain personal data for as long as necessary for the stated purposes or as required by statutory retention obligations.",
        "Account data and content remain available for export for 30 days after the contract ends and are then deleted.",
        "Records of accounting relevance, including invoices and payout evidence, are retained for ten years (Art. 958f CO).",
        "Evidence of consents and electronic signatures is retained for the limitation periods of the relevant claims, generally ten years (Art. 127 CO).",
        "Security and server logs are generally deleted or anonymised after 90 days, unless needed to investigate a specific incident.",
      ],
    },
    {
      h2: "11. Data security",
      body: [
        "We take appropriate technical and organisational measures to protect against unauthorised access, loss and misuse. These include transport encryption, encryption at rest at the hosting provider, tenant-separated storage with row-level access control, least-privilege access restrictions and regular backups.",
        "Absolute security cannot be guaranteed for transmission over the internet.",
        "In the event of a data security breach likely to result in a high risk to the persons concerned, we notify the FDPIC under Art. 24 revFADP and, within the scope of the GDPR, the competent supervisory authority under Art. 33 GDPR and, where required, the data subjects.",
      ],
    },
    {
      h2: "12. Your rights",
      body: [
        "You have the right to information about the personal data we process (Art. 25 revFADP; Art. 15 GDPR).",
        "You have the right to rectification of inaccurate data (Art. 32 revFADP; Art. 16 GDPR) and to erasure and restriction of processing where the statutory conditions are met (Art. 17 and 18 GDPR).",
        "You have the right to receive or transfer your data in a common electronic format (Art. 28 revFADP; Art. 20 GDPR).",
        "You may object to processing where we base it on a legitimate interest (Art. 30(2) revFADP; Art. 21 GDPR).",
        "You may withdraw consent at any time with effect for the future.",
        "A message to the address in section 1 is sufficient to exercise these rights. To prevent misuse we may request proof of identity. We respond within 30 days; if that is not possible we inform you of the reasons and the expected timeframe.",
      ],
    },
    {
      h2: "13. Right to lodge a complaint",
      body: [
        "You have the right to lodge a complaint with a supervisory authority.",
        "In Switzerland: Federal Data Protection and Information Commissioner (FDPIC), Feldeggweg 1, 3003 Bern.",
        "Within the scope of the GDPR: the supervisory authority of your habitual residence, place of work or the place of the alleged infringement.",
      ],
    },
    {
      h2: "14. Automated decisions and profiling",
      body: [
        "We do not take decisions based solely on automated processing that produce legal effects or similarly significantly affect a person within the meaning of Art. 21 revFADP or Art. 22 GDPR.",
        "The Service generates automated assessments, such as an estimate of the fit between companies or a prioritisation of enquiries. These are decision aids for users; a human always makes the decision.",
        "The basis and composition of such assessments are disclosed in the product so that they remain traceable.",
      ],
    },
    {
      h2: "15. Data of visitors to a Twin",
      body: [
        "When you talk to a company's Twin, your inputs and any contact details are transmitted to that company. That company, not us, is responsible for the further processing.",
        "We process this data exclusively on behalf of and on the instructions of that company, and to ensure secure operation.",
        "Exercise your rights against that company. If you nevertheless contact us, we will forward your request or name the responsible entity where we are able to.",
      ],
    },
    {
      h2: "16. Changes to this policy",
      body: [
        "We adapt this policy when our processing, the service providers we use or the legal situation changes.",
        "The version in force is available on this page with a version designation and date. In the case of material changes we additionally inform customers in text form.",
        `Version ${PRIVACY_VERSION}, effective ${PRIVACY_EFFECTIVE_EN}.`,
      ],
    },
    {
      h2: "17. Language versions",
      body: [
        "This Privacy Policy exists in German and English. In the event of discrepancies, the German version prevails.",
        `Contact: ${OP}, ${OPERATOR.email}.`,
      ],
    },
  ],
};
